/**
 * AssetEditorModal — Asset color editor with shade picker, rotate colors, logo swap.
 */
import { useState, useEffect, useRef } from 'react';
import { generateShades } from '../utils/brandUtils.js';

export default function AssetEditorModal({ asset, brandStyle, logoVariants, onClose, onSave }) {
  const [html, setHtml] = useState(asset?.content || '');
  const [originalHtml] = useState(asset?.content || '');
  const [assetColors, setAssetColors] = useState([]); // colors found in the asset
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedColor, setExpandedColor] = useState(null); // which color's shade picker is open
  const iframeRef = useRef(null);

  // Brand colors for palette
  const brandColors = brandStyle ? [
    { label: 'Primary', hex: brandStyle.color_primary },
    { label: 'Secondary', hex: brandStyle.color_secondary },
    { label: 'Accent', hex: brandStyle.color_accent },
    { label: 'Background', hex: brandStyle.color_background },
    { label: 'Surface', hex: brandStyle.color_surface },
    { label: 'Text', hex: brandStyle.color_text },
  ].filter(c => c.hex) : [];

  // Extract colors from the original HTML (not the edited version)
  useEffect(() => {
    if (!originalHtml) return;
    const hexSet = new Set();
    for (const m of originalHtml.matchAll(/#[0-9a-fA-F]{6}/g)) hexSet.add(m[0].toUpperCase());
    for (const m of originalHtml.matchAll(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/g)) {
      hexSet.add('#' + [m[1], m[2], m[3]].map(n => parseInt(n).toString(16).padStart(2, '0')).join('').toUpperCase());
    }
    // Filter to only colors that are close to brand colors (within the palette)
    setAssetColors([...hexSet].slice(0, 10));
  }, [originalHtml]);

  function replaceColor(oldColor, newColor) {
    const regex = new RegExp(oldColor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    setHtml(prev => prev.replace(regex, newColor));
    setHasChanges(true);
    // Update the assetColors list to reflect the change
    setAssetColors(prev => prev.map(c => c === oldColor.toUpperCase() ? newColor.toUpperCase() : c));
  }

  function rotateColors() {
    // Rotate: each brand color in the asset shifts to the next brand color's slot
    const usedBrandHexes = brandColors.map(c => c.hex?.toUpperCase()).filter(Boolean);
    const inAsset = assetColors.filter(c => usedBrandHexes.includes(c));
    if (inAsset.length < 2) return;

    let updated = html;
    // Use placeholder to avoid double-replacement
    const placeholders = inAsset.map((_, i) => `__ROTATE_${i}__`);
    // Step 1: replace each color with a placeholder
    inAsset.forEach((color, i) => {
      updated = updated.replace(new RegExp(color.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), placeholders[i]);
    });
    // Step 2: replace placeholders with rotated colors
    inAsset.forEach((_, i) => {
      const nextColor = inAsset[(i + 1) % inAsset.length];
      updated = updated.replaceAll(placeholders[i], nextColor);
    });
    setHtml(updated);
    setHasChanges(true);
  }

  function swapLogo(variantSvg) {
    try {
      const dataUri = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(variantSvg)));
      setHtml(prev => {
        let updated = prev;
        let swapped = false;
        // 1. Replace existing SVG data URI images
        const newVal = updated.replace(/src=["']data:image\/svg\+xml[^"']*["']/g, () => { swapped = true; return `src="${dataUri}"`; });
        if (swapped) return newVal;
        // 2. Replace img tags with logo/brand in alt text
        const altSwap = updated.replace(/(<img[^>]*alt=["'][^"']*(?:logo|jubly|brand)[^"']*["'][^>]*src=["'])[^"']+/gi, `$1${dataUri}`);
        if (altSwap !== updated) return altSwap;
        // 3. Replace first img tag in the header area (typically the logo)
        return updated.replace(/(<img[^>]*src=["'])[^"']+/, `$1${dataUri}`);
      });
      setHasChanges(true);
    } catch (err) {
      console.error('Logo swap failed:', err);
    }
  }

  function downloadHtml() {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${asset.subtype || 'asset'}.html`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-gray-900">Edit {asset.subtype?.replace(/_/g, ' ')}</h3>
            <span className="text-xs text-gray-400">{asset.asset_type?.replace(/_/g, ' ')}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadHtml} className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50">Download HTML</button>
            {hasChanges && onSave && (
              <button onClick={() => onSave(asset.id, html)} className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-500">Save Changes</button>
            )}
            <button onClick={() => { if (!hasChanges || confirm('Discard changes?')) onClose(); }} className="text-gray-400 hover:text-gray-600 text-xl ml-2">&times;</button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Preview */}
          <div className="flex-1 bg-gray-100 overflow-auto">
            <iframe ref={iframeRef} srcDoc={html} className="w-full h-full border-0 min-h-[500px]" title="Asset Preview" sandbox="allow-same-origin allow-scripts" />
          </div>

          {/* Editor sidebar */}
          <div className="w-72 border-l border-gray-200 p-4 overflow-y-auto flex-shrink-0">
            {/* Rotate colors */}
            <div className="mb-4">
              <button onClick={rotateColors} className="w-full text-xs py-2 border border-indigo-200 text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 font-medium">
                Rotate Color Combinations
              </button>
            </div>

            {/* Colors with shade pickers */}
            <div className="mb-5">
              <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Colors Used</h4>
              <div className="space-y-1">
                {assetColors.map(color => {
                  const isExpanded = expandedColor === color;
                  const shades = generateShades(color);
                  // Find which brand color this is closest to
                  const brandMatch = brandColors.find(b => b.hex?.toUpperCase() === color);
                  return (
                    <div key={color}>
                      <button onClick={() => setExpandedColor(isExpanded ? null : color)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors ${isExpanded ? 'bg-gray-50' : ''}`}>
                        <div className="w-6 h-6 rounded border border-gray-200 flex-shrink-0" style={{ backgroundColor: color }} />
                        <code className="text-[10px] text-gray-500 flex-1 text-left">{color}</code>
                        {brandMatch && <span className="text-[8px] text-gray-400">{brandMatch.label}</span>}
                        <svg className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {isExpanded && (
                        <div className="ml-2 mt-1 mb-2 p-2 bg-gray-50 rounded-lg">
                          <div className="text-[9px] text-gray-400 mb-1.5">Shades</div>
                          <div className="flex gap-1 mb-2">
                            {shades.map((shade, i) => (
                              <button key={i} onClick={() => { replaceColor(color, shade); setExpandedColor(shade); }}
                                className={`w-8 h-8 rounded border transition-transform hover:scale-110 ${shade === color ? 'border-blue-500 ring-1 ring-blue-300' : 'border-gray-200'}`}
                                style={{ backgroundColor: shade }} title={shade} />
                            ))}
                          </div>
                          <div className="text-[9px] text-gray-400 mb-1.5">Swap with brand color</div>
                          <div className="flex gap-1">
                            {brandColors.map(b => (
                              <button key={b.hex} onClick={() => { replaceColor(color, b.hex); setExpandedColor(b.hex); }}
                                className="w-7 h-7 rounded-full border border-gray-200 hover:scale-110 transition-transform" style={{ backgroundColor: b.hex }} title={`${b.label}: ${b.hex}`} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Logo swap */}
            {logoVariants?.length > 0 && (
              <div className="mb-5">
                <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Swap Logo</h4>
                <div className="grid grid-cols-2 gap-1.5">
                  {logoVariants.filter(v => v.key !== 'wide').map(v => (
                    <button key={v.key} onClick={() => swapLogo(v.svg)}
                      className="border border-gray-200 rounded p-1.5 hover:border-blue-400 hover:shadow-sm transition-all"
                      title={v.name}>
                      <div dangerouslySetInnerHTML={{ __html: v.svg }} className="w-full aspect-square [&>svg]:w-full [&>svg]:h-full"
                        style={v.key === 'transparent' ? { backgroundImage: 'linear-gradient(45deg, #eee 25%, transparent 25%), linear-gradient(-45deg, #eee 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eee 75%), linear-gradient(-45deg, transparent 75%, #eee 75%)', backgroundSize: '8px 8px', backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0' } : {}} />
                      <span className="text-[8px] text-gray-400 block mt-0.5 truncate">{v.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Reset */}
            {hasChanges && (
              <button onClick={() => { setHtml(originalHtml); setHasChanges(false); setExpandedColor(null); /* re-extract colors */ const hexSet = new Set(); for (const m of originalHtml.matchAll(/#[0-9a-fA-F]{6}/g)) hexSet.add(m[0].toUpperCase()); setAssetColors([...hexSet].slice(0, 10)); }} className="w-full text-xs py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50">
                Reset to Original
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
